import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Download } from 'lucide-react';

const CallRecordingPlayer = ({ call }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  // Initialize audio when recording URL is available
  useEffect(() => {
    if (call?.recording_url) {
      // Use our backend proxy to get the recording with authentication
      const recordingUrl = `${process.env.REACT_APP_BACKEND_URL}/api/calls/${call.id}/recording`;
      
      if (audioRef.current) {
        audioRef.current.src = recordingUrl;
        audioRef.current.load();
      } else {
        const audio = new Audio(recordingUrl);
        audioRef.current = audio;
        
        audio.addEventListener('loadedmetadata', () => {
          setDuration(audio.duration);
        });
        
        audio.addEventListener('timeupdate', () => {
          setCurrentTime(audio.currentTime);
        });
        
        audio.addEventListener('ended', () => {
          setIsPlaying(false);
          setCurrentTime(0);
        });
      }
    }
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, [call?.id, call?.recording_url]);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(err => {
        console.error('Error playing audio:', err);
        alert('Failed to play recording. Please try again.');
      });
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleDownload = () => {
    if (call?.recording_url) {
      const link = document.createElement('a');
      link.href = `${process.env.REACT_APP_BACKEND_URL}/api/calls/${call.id}/recording`;
      link.download = `recording_${call.id}.mp3`;
      link.click();
    }
  };

  if (!call?.recording_url) {
    return (
      <div className="rounded-lg border bg-muted/50 p-6 text-center text-muted-foreground">
        No recording available for this call
      </div>
    );
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="rounded-lg border bg-card shadow-sm p-4">
      <div className="flex items-center gap-4">
        {/* Play/Pause Button */}
        <button
          onClick={togglePlay}
          className="w-12 h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center justify-center"
          data-testid="play-pause-button"
        >
          {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
        </button>

        {/* Progress Bar */}
        <div className="flex-1">
          <div
            onClick={handleSeek}
            className="h-2 bg-secondary rounded-full cursor-pointer relative"
          >
            <div
              className="h-full bg-primary rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-primary rounded-full shadow-md"
              style={{ left: `${progress}%`, transform: 'translate(-50%, -50%)' }}
            />
          </div>
          <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Download Button */}
        <button
          onClick={handleDownload}
          className="p-3 rounded-lg border hover:bg-secondary transition-colors"
          title="Download recording"
        >
          <Download className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default CallRecordingPlayer;
